import { getDefaultRole, isPortal, ROLE_STORAGE_KEY, type RoleCode } from '@/routes/roles';
import type {
  AmPmStyle,
  DayOfWeekName,
  MonthDisplay,
  NumberFormatStyle,
  TimeFormat,
} from '@/models';
import {
  displayName,
  webRolesOf,
  withMeRoles,
  type MeLinks,
  type UserAccess,
} from '@/auth/userAccess';
import type { FabricaMenus } from '@/auth/fabricaMenus';

/**
 * App-wide client state: who is signed in, which role and portal identity is
 * active, and school-wide display settings. Server data does not belong here —
 * React Query owns that.
 *
 * This module is framework-free (no React) so the reducer and the storage
 * helpers can be unit-tested directly.
 */

export interface AuthUser {
  userId: number;
  name: string;
  email?: string;
}

/** A signed-in mobile number on the public admission portal, with its bearer token. */
export interface ApplicantSession {
  token: string;
  mobile: string;
}

/**
 * School-wide display settings: the school's resolved Global Settings (General,
 * Date & Time), loaded by `useAppSettingsSync`. Defaults match the built-in
 * defaults, which match what the pages rendered before.
 */
export interface AppSettings {
  /** BCP 47 locale for numbers and currency, e.g. `en-IN` (Global Settings › Default Language). */
  locale: string;
  /** ISO 4217 currency code, e.g. `INR` (Global Settings › Currency). */
  currency: string;
  /** dayjs display format for dates, e.g. `DD MMM YYYY`. */
  dateFormat: string;
  shortDateFormat: string;
  monthDisplay: MonthDisplay;
  timeFormat: TimeFormat;
  showSeconds: boolean;
  amPmStyle: AmPmStyle;
  /** IANA time zone that timestamps are shown in. */
  timeZone: string;
  firstDayOfWeek: DayOfWeekName;
  numberFormat: NumberFormatStyle;
  thousandSeparator: string;
  decimalSymbol: string;
  decimalPrecision: number;
  /** Global Settings › Page Size List: the sizes every table offers, e.g. `'20,25,50,75,100'` (see `pageSizesOf`). */
  pageSizeList: string;
  /**
   * `schoolId:organizationVersion.schoolVersion` of the loaded settings, `''` for
   * the defaults. Tells a refresh whether anything changed.
   */
  version: string;
}

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

export interface GlobalState {
  /** Shows the app-wide loading overlay. */
  busy: boolean;
  authRestoring: boolean;
  /** What the overlay says while busy, e.g. "Reading students.csv…". */
  busyLabel: string | undefined;
  error: string | undefined;

  // ── Authentication (filled in once login exists) ─────────────────────────
  user: AuthUser | undefined;
  token: string | undefined;
  /**
   * Who the person is in Camptain's users table (POST /auth/User/ValidateActiveUser): details,
   * schools, the active school and roles, each role with its icon, web home and operations from
   * GET /api/me. Null until B2C sign-in. Not persisted.
   */
  access: UserAccess | null;
  /**
   * The web menus of each of the person's roles, from Fabrica (by role id). Null when not
   * loaded (sample data, or the call failed): the built-in menus show and nothing is blocked.
   * Not persisted.
   */
  menus: FabricaMenus | null;

  /** The public admission portal's OTP-verified mobile session (`/admissionForm/apply`), if any. */
  applicantSession: ApplicantSession | null;

  // ── Who is using the app ─────────────────────────────────────────────────
  /**
   * The active role's code as the API returns it (e.g. `librarian`); in sample data one of the
   * portal names. Its portal comes from its Fabrica web home and menus (see `useRole`).
   */
  activeRole: RoleCode;
  /** Student portal: the student being viewed. */
  selectedStudentId: number | null;
  /** Parent portal: the child being viewed. */
  selectedChildId: number | null;
  /** Faculty portal: the teacher being viewed. */
  selectedStaffId: number | null;
  /**
   * The signed-in person's own records from /api/me: the student they are, a parent's children, the
   * teacher they are. A role switch selects these again, and the family portals scope every read to them.
   */
  links: PortalLinks;

  // ── School context ───────────────────────────────────────────────────────
  /** The school the app works in. `null` follows the default (the organization's first school). */
  activeSchoolId: number | null;
  /** The working academic year (see `useCurrentAcademicYear`). */
  activeAcademicYearId: number | null;
  /**
   * False while `activeAcademicYearId` is only restored from the session and has not been
   * checked against the school's years yet. Any year the app sets itself is confirmed. Not persisted.
   */
  activeAcademicYearConfirmed: boolean;
  appSettings: AppSettings;
  breadcrumb: BreadcrumbItem[];
}

export const DEFAULT_APP_SETTINGS: AppSettings = {
  locale: 'en-IN',
  currency: 'INR',
  dateFormat: 'DD MMM YYYY',
  shortDateFormat: 'DD/MM/YY',
  monthDisplay: 'Short',
  timeFormat: 'TwelveHour',
  showSeconds: false,
  amPmStyle: 'Upper',
  timeZone: 'Asia/Kolkata',
  firstDayOfWeek: 'Sunday',
  numberFormat: 'Indian',
  thousandSeparator: ',',
  decimalSymbol: '.',
  decimalPrecision: 2,
  pageSizeList: '20,25,50,75,100',
  version: '',
};

/** Action names. Strings rather than numbers, so they read well in DevTools. */
export const GlobalAction = {
  Busy: 'Busy',
  Idle: 'Idle',
  Error: 'Error',
  LoggedIn: 'LoggedIn',
  LoggedOut: 'LoggedOut',
  SessionReady: 'SessionReady',
  ApplicantSignedIn: 'ApplicantSignedIn',
  ApplicantSignedOut: 'ApplicantSignedOut',
  AccessLoaded: 'AccessLoaded',
  SetActiveRole: 'SetActiveRole',
  SetSelectedStudent: 'SetSelectedStudent',
  SetSelectedChild: 'SetSelectedChild',
  SetSelectedStaff: 'SetSelectedStaff',
  ClearSelection: 'ClearSelection',
  SetActiveSchool: 'SetActiveSchool',
  SetActiveAcademicYear: 'SetActiveAcademicYear',
  SetAppSettings: 'SetAppSettings',
  SetBreadcrumb: 'SetBreadcrumb',
  ResetAll: 'ResetAll',
} as const;

export type GlobalActionType = (typeof GlobalAction)[keyof typeof GlobalAction];

export type GlobalStateAction =
  | { type: typeof GlobalAction.Busy; label?: string }
  | { type: typeof GlobalAction.Idle }
  | { type: typeof GlobalAction.Error; error: unknown }
  | {
      type: typeof GlobalAction.LoggedIn;
      user: AuthUser;
      token?: string;
    }
  | { type: typeof GlobalAction.LoggedOut }
  | { type: typeof GlobalAction.SessionReady }
  | { type: typeof GlobalAction.ApplicantSignedIn; session: ApplicantSession }
  | { type: typeof GlobalAction.ApplicantSignedOut }
  | {
      type: typeof GlobalAction.AccessLoaded;
      access: UserAccess;
      token: string;
      links?: MeLinks;
      menus?: FabricaMenus | null;
      schoolId?: number | null;
    }
  | { type: typeof GlobalAction.SetActiveRole; role: RoleCode }
  | { type: typeof GlobalAction.SetSelectedStudent; studentId: number | null }
  | { type: typeof GlobalAction.SetSelectedChild; studentId: number | null }
  | { type: typeof GlobalAction.SetSelectedStaff; staffId: number | null }
  | { type: typeof GlobalAction.ClearSelection }
  | { type: typeof GlobalAction.SetActiveSchool; schoolId: number | null }
  | { type: typeof GlobalAction.SetActiveAcademicYear; yearId: number | null }
  | { type: typeof GlobalAction.SetAppSettings; appSettings: Partial<AppSettings> }
  | { type: typeof GlobalAction.SetBreadcrumb; breadcrumb: BreadcrumbItem[] }
  | { type: typeof GlobalAction.ResetAll };

/** The signed-in person's own records (see `GlobalState.links`). */
export interface PortalLinks {
  studentId: number | null;
  childIds: number[];
  staffId: number | null;
}

export const NO_LINKS: PortalLinks = { studentId: null, childIds: [], staffId: null };

const NO_SELECTION = {
  selectedStudentId: null,
  selectedChildId: null,
  selectedStaffId: null,
} satisfies Partial<GlobalState>;

/** Their own records selected: the student themself, a parent's first child, the teacher themself. */
const ownSelection = (links: PortalLinks) => ({
  selectedStudentId: links.studentId,
  selectedChildId: links.childIds[0] ?? null,
  selectedStaffId: links.staffId,
});

/** A clean state for `role`, with nothing signed in or selected. */
export function createInitialState(role: RoleCode = getDefaultRole()): GlobalState {
  return {
    busy: false,
    authRestoring: true,
    busyLabel: undefined,
    error: undefined,
    user: undefined,
    token: undefined,
    access: null,
    menus: null,
    applicantSession: null,
    activeRole: role,
    ...NO_SELECTION,
    links: NO_LINKS,
    activeSchoolId: null,
    activeAcademicYearId: null,
    activeAcademicYearConfirmed: false,
    appSettings: DEFAULT_APP_SETTINGS,
    breadcrumb: [],
  };
}

const errorMessage = (error: unknown): string =>
  error instanceof Error
    ? error.message
    : typeof error === 'string'
      ? error
      : 'Something went wrong';

export function globalReducer(state: GlobalState, action: GlobalStateAction): GlobalState {
  switch (action.type) {
    case GlobalAction.Busy:
      return { ...state, busy: true, busyLabel: action.label };
    case GlobalAction.Idle:
      return { ...state, busy: false, busyLabel: undefined };
    case GlobalAction.Error:
      return { ...state, busy: false, busyLabel: undefined, error: errorMessage(action.error) };

    case GlobalAction.LoggedIn:
      return {
        ...state,
        user: action.user,
        token: action.token,
        error: undefined,
      };
    case GlobalAction.SessionReady:
      return { ...state, authRestoring: false };
    case GlobalAction.LoggedOut:
      // Selections and school settings are user/tenant scoped: never let them
      // carry over to the next person on a shared computer.
      return {
        ...state,
        user: undefined,
        token: undefined,
        applicantSession: null,
        authRestoring: false,
        // Signed out, the app shows sample data: the active role must name one of its portals.
        activeRole: isPortal(state.activeRole) ? state.activeRole : getDefaultRole(),
        access: null,
        menus: null,
        ...NO_SELECTION,
        links: NO_LINKS,
        activeSchoolId: null,
        activeAcademicYearId: null,
        appSettings: DEFAULT_APP_SETTINGS,
        breadcrumb: [],
      };
    case GlobalAction.ApplicantSignedIn:
      return { ...state, applicantSession: action.session };
    case GlobalAction.ApplicantSignedOut:
      return { ...state, applicantSession: null };
    case GlobalAction.AccessLoaded: {
      // The signed-in person and their roles drive the portals and menus; /api/me adds each
      // role's icon, web home and operations. A role opens on the web when it has a web home.
      const access: UserAccess = {
        ...action.access,
        roles: withMeRoles(action.access.roles, action.links?.roles),
      };
      const roles = webRolesOf(access, action.menus ?? null).map((r) => r.code);
      const activeRole = roles.includes(state.activeRole)
        ? state.activeRole
        : (roles[0] ?? state.activeRole);
      const school =
        state.access?.activeOrganization.organizationId !==
          action.access.activeOrganization.organizationId ||
        state.access?.branchId !== action.access.branchId;
      const links: PortalLinks = {
        studentId: action.links?.studentId ?? null,
        childIds: (action.links?.children ?? []).map((c) => c.studentId),
        staffId: action.links?.staffId ?? null,
      };
      return {
        ...state,
        access,
        menus: action.menus ?? null,
        token: action.token,
        user: {
          userId: action.access.user.userId,
          name: displayName(action.access),
          ...(action.access.user.emailAddress ? { email: action.access.user.emailAddress } : {}),
        },
        activeRole,
        // Their own records: the student themself, a parent's first child, the teacher themself.
        selectedStudentId:
          action.links?.studentId ??
          (school || activeRole !== state.activeRole ? null : state.selectedStudentId),
        selectedChildId:
          action.links?.children?.[0]?.studentId ??
          (school || activeRole !== state.activeRole ? null : state.selectedChildId),
        selectedStaffId:
          action.links?.staffId ??
          (school || activeRole !== state.activeRole ? null : state.selectedStaffId),
        links,
        ...(school
          ? {
              activeSchoolId: null,
              activeAcademicYearId: null,
              activeAcademicYearConfirmed: false,
              appSettings: DEFAULT_APP_SETTINGS,
              breadcrumb: [],
            }
          : {}),
        ...(action.schoolId !== undefined ? { activeSchoolId: action.schoolId } : {}),
        error: undefined,
      };
    }

    case GlobalAction.SetActiveRole:
      // A different role views different records: drop the previous selection and select their own
      // again, so a student or parent role never falls back to the whole school's lists.
      return action.role === state.activeRole
        ? state
        : { ...state, activeRole: action.role, ...ownSelection(state.links) };

    case GlobalAction.SetSelectedStudent:
      return { ...state, selectedStudentId: action.studentId };
    case GlobalAction.SetSelectedChild:
      return { ...state, selectedChildId: action.studentId };
    case GlobalAction.SetSelectedStaff:
      return { ...state, selectedStaffId: action.staffId };
    case GlobalAction.ClearSelection:
      return { ...state, ...NO_SELECTION };

    case GlobalAction.SetActiveSchool:
      // Academic years belong to a school: a year chosen in another school no longer applies.
      return action.schoolId === state.activeSchoolId
        ? state
        : { ...state, activeSchoolId: action.schoolId, activeAcademicYearId: null };
    case GlobalAction.SetActiveAcademicYear:
      // Every page setting the same working year must not re-render the app.
      return action.yearId === state.activeAcademicYearId && state.activeAcademicYearConfirmed
        ? state
        : { ...state, activeAcademicYearId: action.yearId, activeAcademicYearConfirmed: true };
    case GlobalAction.SetAppSettings:
      return { ...state, appSettings: { ...state.appSettings, ...action.appSettings } };
    case GlobalAction.SetBreadcrumb:
      return { ...state, breadcrumb: action.breadcrumb };

    case GlobalAction.ResetAll:
      return createInitialState();

    default:
      return state;
  }
}

// ── Persistence ────────────────────────────────────────────────────────────
// The role lives in localStorage (a temporary picker until login exists).
// Portal identity, academic year and settings live in sessionStorage: they
// survive a refresh but not a closed browser or the next user.

export const SESSION_STORAGE_KEY = 'camptain.session';

type PersistedSession = Pick<
  GlobalState,
  | 'selectedStudentId'
  | 'selectedChildId'
  | 'selectedStaffId'
  | 'activeSchoolId'
  | 'activeAcademicYearId'
  | 'appSettings'
  | 'applicantSession'
>;

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const browserStorage = (kind: 'localStorage' | 'sessionStorage'): StorageLike | undefined => {
  try {
    return typeof window === 'undefined' ? undefined : window[kind];
  } catch {
    return undefined;
  }
};

const idOrNull = (value: unknown): number | null =>
  typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;

// Each stored field is kept only when it has the default's type; anything else falls back.
const settingsOrDefault = (value: unknown): AppSettings => {
  if (!value || typeof value !== 'object') return DEFAULT_APP_SETTINGS;
  const stored = value as Record<string, unknown>;
  const result: Record<string, unknown> = { ...DEFAULT_APP_SETTINGS };
  for (const [key, fallback] of Object.entries(DEFAULT_APP_SETTINGS)) {
    if (typeof stored[key] === typeof fallback) result[key] = stored[key];
  }
  return result as unknown as AppSettings;
};

const isDefaultSettings = (s: AppSettings): boolean =>
  (Object.keys(DEFAULT_APP_SETTINGS) as (keyof AppSettings)[]).every(
    (k) => s[k] === DEFAULT_APP_SETTINGS[k],
  );

const applicantSessionOrNull = (value: unknown): ApplicantSession | null => {
  if (!value || typeof value !== 'object') return null;
  const v = value as Partial<Record<keyof ApplicantSession, unknown>>;
  return typeof v.token === 'string' && typeof v.mobile === 'string'
    ? { token: v.token, mobile: v.mobile }
    : null;
};

/** Rebuilds state from storage. Anything missing or malformed falls back to defaults. */
export function loadInitialState(
  local: StorageLike | undefined = browserStorage('localStorage'),
  session: StorageLike | undefined = browserStorage('sessionStorage'),
): GlobalState {
  let role: RoleCode = getDefaultRole();
  let saved: Partial<Record<keyof PersistedSession, unknown>> = {};
  try {
    const storedRole = local?.getItem(ROLE_STORAGE_KEY);
    // Any role code: sign-in (restored from MSAL's cache on start) replaces one the person no longer
    // has, and sample data shows the default portal for one that names none (see useRole).
    if (typeof storedRole === 'string' && storedRole.trim()) role = storedRole;
    const raw = session?.getItem(SESSION_STORAGE_KEY);
    if (raw) saved = JSON.parse(raw) as typeof saved;
  } catch {
    // Unavailable storage or malformed JSON: start clean.
  }

  return {
    ...createInitialState(role),
    selectedStudentId: idOrNull(saved.selectedStudentId),
    selectedChildId: idOrNull(saved.selectedChildId),
    selectedStaffId: idOrNull(saved.selectedStaffId),
    activeSchoolId: idOrNull(saved.activeSchoolId),
    activeAcademicYearId: idOrNull(saved.activeAcademicYearId),
    appSettings: settingsOrDefault(saved.appSettings),
    applicantSession: applicantSessionOrNull(saved.applicantSession),
  };
}

/** Writes the persisted slices of `state`. Storage failures never break the app. */
export function persistState(
  state: GlobalState,
  local: StorageLike | undefined = browserStorage('localStorage'),
  session: StorageLike | undefined = browserStorage('sessionStorage'),
): void {
  try {
    local?.setItem(ROLE_STORAGE_KEY, state.activeRole);
    const persisted: PersistedSession = {
      selectedStudentId: state.selectedStudentId,
      selectedChildId: state.selectedChildId,
      selectedStaffId: state.selectedStaffId,
      activeSchoolId: state.activeSchoolId,
      activeAcademicYearId: state.activeAcademicYearId,
      appSettings: state.appSettings,
      applicantSession: state.applicantSession,
    };
    const isEmpty =
      persisted.selectedStudentId === null &&
      persisted.selectedChildId === null &&
      persisted.selectedStaffId === null &&
      persisted.activeSchoolId === null &&
      persisted.activeAcademicYearId === null &&
      persisted.applicantSession === null &&
      isDefaultSettings(persisted.appSettings);
    if (isEmpty) session?.removeItem(SESSION_STORAGE_KEY);
    else session?.setItem(SESSION_STORAGE_KEY, JSON.stringify(persisted));
  } catch {
    // Persistence is best-effort.
  }
}
